import React, { Fragment, useEffect, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import {
 Badge, Button, Card, Col, Dropdown, Form,
 Modal, Row, Spinner, Table,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";
import swal from "sweetalert";
import VerifiedCopyActions from "../Common/VerifiedCopyActions";
import PortalMenu from "../Common/PortalMenu";
import SignatureModal from "../Common/SignatureModal";

const statusVariant = (status) => {
 if (status === "Approved") return "success";
 if (status === "Rejected") return "danger";
 return "warning";
};

const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24" aria-hidden="true">
 <circle fill="#000000" cx="12" cy="5" r="2" />
 <circle fill="#000000" cx="12" cy="12" r="2" />
 <circle fill="#000000" cx="12" cy="19" r="2" />
 </svg>
);

const ContractorJMRList = () => {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const cwoFromUrl = searchParams.get("contractualWorkOrder") || "";
 const workOrderFromUrl = searchParams.get("workOrder") || "";

 const [jmrs, setJMRs] = useState([]);
 const [loading, setLoading] = useState(true);
 const [pdfLoadingId, setPdfLoadingId] = useState(null); // tracks which JMR PDF is loading

 // Approval modal
 const [selectedJMR, setSelectedJMR] = useState(null);
 const [showApprovalModal, setShowApprovalModal] = useState(false);
 const [approvalRemarks, setApprovalRemarks] = useState("");
 const [approvalAction, setApprovalAction] = useState("");
 const [submitting, setSubmitting] = useState(false);

 // Signature modal (for approve)
 const [showSignModal, setShowSignModal] = useState(false);

 // View details modal
 const [viewJMR, setViewJMR] = useState(null);

 const [filters, setFilters] = usePersistentFilters("contractor_jmr_filters", { search: "", approvalStatus: "" });

 // Load 
 const loadJMRs = async () => {
 try {
 setLoading(true);
 const query = new URLSearchParams({ limit: "1000" });
 if (cwoFromUrl) query.set("contractualWorkOrder", cwoFromUrl);
 if (workOrderFromUrl) query.set("workOrder", workOrderFromUrl);
 if (filters.search) query.set("search", filters.search);
 if (filters.approvalStatus) query.set("approvalStatus", filters.approvalStatus);
 const res = await api.get(`/contractor-jmrs?${query.toString()}`);
 if (res.data.success) setJMRs(res.data.data || []);
 } catch {
 swal("Error", "Failed to load Contractor JMRs", "error");
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 loadJMRs();
 }, [cwoFromUrl, workOrderFromUrl]);

 // Approval 
 const openApprovalModal = (jmr, action) => {
 setSelectedJMR(jmr);
 setApprovalAction(action);
 setApprovalRemarks("");
 if (action === "approve") {
 setShowSignModal(true);
 } else {
 setShowApprovalModal(true);
 }
 };

 const closeApprovalModal = () => {
 setShowApprovalModal(false);
 setShowSignModal(false);
 setSelectedJMR(null);
 setApprovalAction("");
 };

 const handleApproveWithSignature = async (signatureFile, _stamp, remarks) => {
 if (!selectedJMR) return;
 try {
 setSubmitting(true);
 setShowSignModal(false);

 const formData = new FormData();
 formData.append("approvalRemarks", remarks || "");
 if (signatureFile) formData.append("approvalSignature", signatureFile);

 const res = await api.patch(
 `/contractor-jmrs/${selectedJMR._id}/approve`,
 formData,
 { headers: { "Content-Type": "multipart/form-data" } }
 );

 if (res.data.success) {
 swal("Success", "Contractor JMR approved successfully", "success");
 setSelectedJMR(null);
 loadJMRs();
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Approval failed", "error");
 } finally {
 setSubmitting(false);
 }
 };

 const handleReject = async () => {
 if (!selectedJMR) return;
 try {
 setSubmitting(true);
 const res = await api.patch(`/contractor-jmrs/${selectedJMR._id}/reject`, {
 approvalRemarks,
 });
 if (res.data.success) {
 swal("Success", "Contractor JMR rejected", "success");
 closeApprovalModal();
 loadJMRs();
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Rejection failed", "error");
 } finally {
 setSubmitting(false);
 }
 };

 // Delete 
 const handleDelete = async (jmr) => {
 const confirm = await swal({
 title: "Confirm Delete",
 text: `Are you sure you want to delete Contractor JMR ${jmr.jmrNumber}?`,
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 const res = await api.delete(`/contractor-jmrs/${jmr._id}`);
 if (res.data.success) {
 swal("Success", "Contractor JMR deleted successfully", "success");
 loadJMRs();
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to delete", "error");
 }
 };

 // View Details 
 const handleView = async (jmr) => {
 try {
 const res = await api.get(`/contractor-jmrs/${jmr._id}`);
 if (res.data.success) setViewJMR(res.data.data);
 } catch {
 swal("Error", "Failed to load JMR details", "error");
 }
 };

 // PDF Preview 
 const handlePreviewPdf = async (jmr) => {
 try {
 setPdfLoadingId(jmr._id);
 const res = await api.get(`/contractor-jmrs/${jmr._id}/pdf`, {
 responseType: "blob",
 });
 const url = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" })
 );
 window.open(url, "_blank");
 } catch {
 swal("Error", "Failed to generate PDF", "error");
 } finally {
 setPdfLoadingId(null);
 }
 };

 const handleSearch = () => loadJMRs();

 return (
 <Fragment>
 <PageTitle activeMenu="Contractor JMR" motherMenu="Commercial" />

 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
 <Card.Title className="mb-0">Contractor JMR List</Card.Title>
 <div className="d-flex gap-2">
 <Button variant="primary" onClick={() => navigate("/contractual-debit")}>
 Contractual Work Orders
 </Button>
 <Button variant="primary" onClick={() => navigate("/contractor-jmr/add")}>
 <i className="fa fa-plus me-2"></i>Create JMR
 </Button>
 </div>
 </Card.Header>

 {cwoFromUrl && (
 <Card.Body className="pb-0">
 <div className="alert alert-info mb-0">Filtered by contractual work order.</div>
 </Card.Body>
 )}

 <Card.Body>
 <Row className="g-2 mb-3 align-items-end">
 <Col md={5}>
 <Form.Control
 placeholder="Search JMR number, location, remarks..."
 value={filters.search}
 onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
 onKeyDown={(e) => e.key === "Enter" && handleSearch()}
 />
 </Col>
 <Col md={3}>
 <Form.Select
 value={filters.approvalStatus}
 onChange={(e) => setFilters((p) => ({ ...p, approvalStatus: e.target.value }))}
 >
 <option value="">All Status</option>
 <option value="Pending">Pending</option>
 <option value="Approved">Approved</option>
 <option value="Rejected">Rejected</option>
 </Form.Select>
 </Col>
 <Col md="auto">
 <Button variant="primary" onClick={handleSearch}>Search</Button>
 </Col>
 </Row>

 {loading ? (
 <div className="text-center py-5"><Spinner /></div>
 ) : (
 <div className="table-responsive">
 <Table hover bordered className="align-middle text-center">
 <thead>
 <tr>
 <th>JMR No</th>
 <th>CWO No</th>
 <th>Contractor</th>
 <th>Measurement Date</th>
 <th>Items</th>
 <th>Status</th>
 <th className="text-center">Actions</th>
 </tr>
 </thead>
 <tbody>
 {jmrs.map((jmr) => (
 <tr key={jmr._id}>
 <td className="align-middle">
 <strong>{jmr.jmrNumber}</strong>
 <div className="small text-muted">
 {new Date(jmr.createdAt).toLocaleDateString("en-IN")}
 </div>
 </td>
 <td className="align-middle">
 <strong>{jmr.contractualWorkOrder?.cwoNo || "—"}</strong>
 </td>
 <td className="align-middle">{jmr.vendor?.name || "—"}</td>
 <td className="align-middle">
 {new Date(jmr.measurementDate).toLocaleDateString("en-IN")}
 </td>
 <td className="align-middle">{jmr.items?.length || 0}</td>
 <td className="align-middle">
 <Badge bg={statusVariant(jmr.approvalStatus)}>
 {jmr.approvalStatus}
 </Badge>
 </td>
 <td className="align-middle overflow-visible">
 <div className="d-flex justify-content-end align-items-center gap-2 flex-nowrap">
 <VerifiedCopyActions
 id={jmr._id}
 type="contractor-jmrs"
 moduleKey="contractor-jmrs"
 hasVerifiedCopy={Boolean(
 jmr.verifiedCopy?.filePath || jmr.verifiedCopy?.fileUrl
 )}
 verifiedCopy={jmr.verifiedCopy}
 onUploadSuccess={loadJMRs}
 canUpload={true}
 />

 <Dropdown>
 <Dropdown.Toggle
 variant=""
 className="btn-link i-false p-0 cursor-pointer ms-auto"
 as="div"
 title="Click for more actions"
 >
 {svgMenu}
 </Dropdown.Toggle>
 <Dropdown.Menu align="end" as={PortalMenu}>
 <Dropdown.Item onClick={() => handleView(jmr)}>
 <i className="fas fa-eye me-2 text-primary"></i>
 View Details
 </Dropdown.Item>

 <Dropdown.Item
 onClick={() => handlePreviewPdf(jmr)}
 disabled={pdfLoadingId === jmr._id}
 >
 {pdfLoadingId === jmr._id
 ? <><Spinner size="sm" className="me-2" />Generating...</>
 : <><i className="fas fa-file-pdf me-2 text-danger"></i>Preview PDF</>
 }
 </Dropdown.Item>

 <Dropdown.Item onClick={() => navigate(`/contractor-jmr/edit/${jmr._id}`)}>
 <i className="fas fa-edit me-2 text-warning"></i>
 Edit
 </Dropdown.Item>

 {jmr.approvalStatus === "Pending" && (
 <>
 <Dropdown.Divider />
 <Dropdown.Item onClick={() => openApprovalModal(jmr, "approve")}>
 <i className="fas fa-check me-2 text-success"></i>
 Approve
 </Dropdown.Item>
 <Dropdown.Item onClick={() => openApprovalModal(jmr, "reject")}>
 <i className="fas fa-times me-2 text-warning"></i>
 Reject
 </Dropdown.Item>
 </>
 )}

 <Dropdown.Divider />
 <Dropdown.Item className="text-danger" onClick={() => handleDelete(jmr)}>
 <i className="fas fa-trash me-2"></i>
 Delete
 </Dropdown.Item>
 </Dropdown.Menu>
 </Dropdown>
 </div>
 </td>
 </tr>
 ))}
 {jmrs.length === 0 && (
 <tr>
 <td colSpan="7" className="text-center text-muted py-4">
 No contractor JMRs found.
 </td>
 </tr>
 )}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>

 {/* Reject Remarks Modal */}
 <Modal show={showApprovalModal} onHide={closeApprovalModal} centered>
 <Modal.Header closeButton>
 <Modal.Title>Reject Contractor JMR</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {selectedJMR && (
 <>
 <div className="mb-2"><strong>JMR No:</strong> {selectedJMR.jmrNumber}</div>
 <div className="mb-3">
 <strong>CWO No:</strong> {selectedJMR.contractualWorkOrder?.cwoNo || "—"}
 </div>
 <Form.Group>
 <Form.Label>Rejection Remarks (Optional)</Form.Label>
 <Form.Control
 as="textarea"
 rows={3}
 placeholder="Enter remarks..."
 value={approvalRemarks}
 onChange={(e) => setApprovalRemarks(e.target.value)}
 />
 </Form.Group>
 </>
 )}
 </Modal.Body>
 <Modal.Footer>
 <Button variant="light" onClick={closeApprovalModal}>Cancel</Button>
 <Button variant="warning" onClick={handleReject} disabled={submitting}>
 {submitting ? <><Spinner size="sm" className="me-2" />Processing...</> : "Reject"}
 </Button>
 </Modal.Footer>
 </Modal>

 {/* Signature Modal (Approve) */}
 <SignatureModal
 show={showSignModal}
 onHide={closeApprovalModal}
 onSave={handleApproveWithSignature}
 title="Approve Contractor JMR — Signature Required"
 />

 {/* View Details Modal */}
 <Modal show={!!viewJMR} onHide={() => setViewJMR(null)} size="lg" centered> <Modal.Header closeButton>
 <Modal.Title>Contractor JMR — {viewJMR?.jmrNumber}</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {viewJMR && (
 <>
 <Row className="mb-3">
 <Col md={6}>
 <p><strong>JMR No:</strong> {viewJMR.jmrNumber}</p>
 <p>
 <strong>CWO No:</strong>{" "}
 {viewJMR.contractualWorkOrder?.cwoNo || "—"}
 
 </p>
 <p><strong>Contractor:</strong> {viewJMR.vendor?.name || "—"}</p>
 <p>
 <strong>Status:</strong>{" "}
 <Badge bg={statusVariant(viewJMR.approvalStatus)}>
 {viewJMR.approvalStatus}
 </Badge>
 </p>
 </Col>
 <Col md={6}>
 <p>
 <strong>Measurement Date:</strong>{" "}
 {new Date(viewJMR.measurementDate).toLocaleDateString("en-IN")}
 </p>
 <p><strong>Submitted By:</strong> {viewJMR.submittedBy?.name || "—"}</p>
 {viewJMR.approvalStatus === "Approved" && (
 <p><strong>Approved By:</strong> {viewJMR.approvedBy?.name || "—"}</p>
 )}
 {viewJMR.remarks && (
 <p><strong>Remarks:</strong> {viewJMR.remarks}</p>
 )}
 {viewJMR.jmrDocument?.fileUrl && (
 <p>
 <strong>JMR Document:</strong>{" "}
 <a
 href={getFileUrl(viewJMR.jmrDocument.fileUrl)}
 target="_blank"
 rel="noreferrer"
 className="btn btn-sm btn-outline-primary ms-2"
 >
 <i className="fa fa-file-alt me-1"></i>
 {viewJMR.jmrDocument.fileName || "View Document"}
 </a>
 </p>
 )}
 </Col>
 </Row>
 <hr />
 <Table bordered size="sm">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>Item</th>
 <th>Unit</th>
 <th>CWO Qty</th>
 <th>Prev Measured</th>
 <th>This JMR</th>
 <th>Location</th>
 </tr>
 </thead>
 <tbody>
 {(viewJMR.items || []).map((item, i) => (
 <tr key={i}>
 <td>{i + 1}</td>
 <td>
 <strong>{item.itemName}</strong>
 {item.description && (
 <div className="small text-muted">{item.description}</div>
 )}
 </td>
 <td>{item.unit || "—"}</td>
 <td>{item.woQuantity}</td>
 <td>{item.previousQuantity}</td>
 <td><strong className="text-primary">{item.measuredQuantity}</strong></td>
 <td>
 {item.location ||
 (item.measurements?.filter((m) => m.location).map((m) => m.location).join(", ")) ||
 "—"}
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </>
 )}
 </Modal.Body>
 <Modal.Footer>
 <Button variant="light" onClick={() => setViewJMR(null)}>Close</Button>
 </Modal.Footer>
 </Modal>

 {/* PDF Loading Overlay */}
 {pdfLoadingId && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{ background: "rgba(0,0,0,0.45)", zIndex: 9999 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <Spinner animation="border" variant="primary" className="mb-3" />
 <h6 className="mb-0">Preparing PDF, please wait…</h6>
 </div>
 </div>
 )}

 </Fragment>
 );
};

export default ContractorJMRList;
